import { useEffect, useRef, useCallback } from 'react';
import { gsap } from 'gsap';

interface LoadingAnimationProps {
	onComplete?: () => void;
}

const LOADING_STEPS = [0, 33, 56, 75, 99];
const STEP_DURATION = 0.4;
const FINAL_HOLD = 0.4;
const FADE_DURATION = 0.25;
const TOP_MARGIN_PERCENT = 2;
const DEFAULT_ELEMENT_HEIGHT = 140;

const prefersReducedMotion =
	typeof window !== 'undefined' &&
	window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function LoadingAnimation({
	onComplete,
}: LoadingAnimationProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const numberRefs = useRef<(HTMLDivElement | null)[]>([]);
	const progressBarRef = useRef<HTMLDivElement>(null);
	const timelineRef = useRef<gsap.core.Timeline | null>(null);
	const hasCompletedRef = useRef(false);

	const handleComplete = useCallback(() => {
		if (hasCompletedRef.current) {
			return;
		}
		hasCompletedRef.current = true;
		onComplete?.();
	}, [onComplete]);

	useEffect(() => {
		if (prefersReducedMotion) {
			handleComplete();
			return;
		}

		const container = containerRef.current;
		const numbers = numberRefs.current;
		const progressBar = progressBarRef.current;

		if (!container || !progressBar) {
			return;
		}

		const validNumbers = numbers.filter(Boolean) as HTMLDivElement[];
		if (validNumbers.length === 0) {
			return;
		}

		if (timelineRef.current) {
			timelineRef.current.kill();
		}

		const viewportHeight = window.innerHeight;
		const viewportWidth = window.innerWidth;
		const topMarginPx = (TOP_MARGIN_PERCENT / 100) * viewportHeight;
		const leftMarginPx =
			viewportWidth >= 768 ? 32 : viewportWidth >= 640 ? 24 : 16;

		const lastElementHeight =
			numbers[LOADING_STEPS.length - 1]?.offsetHeight || DEFAULT_ELEMENT_HEIGHT;
		const availableHeight = viewportHeight - topMarginPx - lastElementHeight;
		const equalSpacing = availableHeight / (LOADING_STEPS.length - 1);

		numbers.forEach((num, index) => {
			if (!num) return;
			const isLast = index === LOADING_STEPS.length - 1;
			gsap.set(num, {
				opacity: 0,
				left: `${leftMarginPx}px`,
				...(isLast
					? { bottom: 0 }
					: { top: `${topMarginPx + index * equalSpacing}px` }),
			});
		});

		gsap.set(progressBar, { opacity: 1, top: 0, right: 0, height: 0 });

		const tl = gsap.timeline({
			onComplete: handleComplete,
		});

		timelineRef.current = tl;

		const lastStepStart = (LOADING_STEPS.length - 1) * STEP_DURATION;
		const animationEnd = lastStepStart + STEP_DURATION;

		LOADING_STEPS.forEach((_, index) => {
			const numberEl = numbers[index];

			if (!numberEl) {
				return;
			}

			const stepStart = index * STEP_DURATION;
			const progressHeight =
				index === LOADING_STEPS.length - 1
					? viewportHeight
					: topMarginPx + index * equalSpacing + lastElementHeight;

			tl.to(
				progressBar,
				{
					height: `${progressHeight}px`,
					duration: STEP_DURATION,
					ease: 'power1.out',
				},
				stepStart,
			);

			tl.set(numberEl, { opacity: 1 }, stepStart);

			if (index < LOADING_STEPS.length - 1) {
				tl.set(numberEl, { opacity: 0 }, stepStart + STEP_DURATION);
			}
		});

		tl.to(
			container,
			{
				opacity: 0,
				duration: FADE_DURATION,
				ease: 'power2.inOut',
			},
			animationEnd + FINAL_HOLD,
		);

		return () => {
			if (timelineRef.current) {
				timelineRef.current.kill();
				timelineRef.current = null;
			}
		};
	}, [handleComplete]);

	return (
		<div
			ref={containerRef}
			className="fixed inset-0 z-[100] bg-background"
			role="status"
			aria-label="Loading website"
			aria-live="polite"
		>
			{LOADING_STEPS.map((number, index) => (
				<div
					key={number}
					ref={(el) => {
						numberRefs.current[index] = el;
					}}
					className="absolute text-[6rem] sm:text-[7rem] md:text-[9rem] font-bold text-primary font-sans tracking-tight"
				>
					{number}
				</div>
			))}
			<div ref={progressBarRef} className="absolute w-2.5 bg-primary" />
			<div className="absolute top-0 left-0 p-4 sm:p-6 md:p-8 text-xs sm:text-sm font-mono text-primary">
				larskemper.dev / {new Date().getFullYear()}
			</div>
		</div>
	);
}
